import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Avatar, Box, Button, Chip, CircularProgress, IconButton, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import JornadaService from "../../services/JornadaService";
import LogService from "../../services/LogService";
import ILog from "../../types/Log";
import IAlert from "../../types/Alert";
import { IDiaFichajes, IJornadaEvento, IJornadaRegistros, IPersonaRegistros, SituacionPersona } from "../../types/Jornada";
import { aHHmm, diaSemanaCorto, diferenciaFichada, duracionFichada, horaFichaje, mesAnterior, mesDe, NOMBRE_FICHAJE, nombreMes, siguienteMes, textoAnulado, textoCorreccion } from "../../utils/Jornada";
import CifrasJornada, { colorDiferencia } from "./CifrasJornada";
import { ChipAnulado } from "./ChipFichaje";
import CorreccionDialog, { PeticionCorreccion } from "./CorreccionDialog";
import ExportarJornada from "./ExportarJornada";
import * as constantes from "../../common/Constantes";

// Pestaña «Registros» (admin): ¿qué pasó en un mes? Todo el equipo en un resumen por persona y, al pulsar una, su detalle
// día a día, con cada corrección (el original, quién y por qué). Salen también quienes ya no fichan o están de baja si tienen
// fichajes ese mes. Con el módulo apagado, en solo lectura.
interface RegistrosJornadaProps {
    setAlert: (alert: IAlert) => void;
}

type Fallo = { message?: string; response?: { status?: number; data?: { message?: string } } };

const SITUACION: Record<Exclude<SituacionPersona, null>, string> = { baja: 'De baja', sinAcceso: 'Sin acceso', noFicha: 'No ficha' };
const nombreDe = (p: { firstName: string; lastName?: string }) => `${p.firstName} ${p.lastName ?? ''}`.trim();
const iniciales = (p: { firstName: string; lastName?: string }) => `${p.firstName?.[0] ?? ''}${p.lastName?.[0] ?? ''}`.toUpperCase();

const RegistrosJornada = ({ setAlert }: RegistrosJornadaProps) => {
    const history = useNavigate();
    const esMovil = useMediaQuery(useTheme().breakpoints.down('sm'));
    const [mes, setMes] = useState<string | null>(null); // null hasta saber el mes de hoy en la clínica
    const [datos, setDatos] = useState<IJornadaRegistros | null>(null);
    const [loading, setLoading] = useState(true);
    const [persona, setPersona] = useState<number | null>(null); // null: todo el equipo
    const [peticion, setPeticion] = useState<PeticionCorreccion | null>(null);
    // Menú de acciones de un fichaje (corregir, confirmar, anular)
    const [menu, setMenu] = useState<{ ancla: HTMLElement; evento: IJornadaEvento; fecha: string } | null>(null);

    const cargar = useCallback((elegido: string | null) => {
        setLoading(true);
        // La primera vez se pide el mes del dispositivo y la API dice cuál es hoy en la clínica
        const hoyDispositivo = new Date();
        const consulta = elegido ?? `${hoyDispositivo.getFullYear()}-${String(hoyDispositivo.getMonth() + 1).padStart(2, '0')}`;
        JornadaService.getRegistros(consulta)
            .then((response: { data: IJornadaRegistros }) => {
                setDatos(response.data);
                setMes(consulta);
            })
            .catch((e: unknown) => {
                const fallo = e as Fallo;
                if (fallo.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                    return;
                }
                if (![400, 403].includes(fallo.response?.status ?? 0)) {
                    const error: ILog = { method: 'RegistrosJornada.getRegistros', level: 'error', message: fallo.message ?? '', meta: fallo.response };
                    LogService.logError(error);
                }
                setAlert({ open: true, severity: 'error', msg: fallo.response?.data?.message || 'No se han podido cargar los registros' });
            })
            .finally(() => setLoading(false));
    }, [history, setAlert]);

    useEffect(() => { cargar(null); }, [cargar]);

    const guardado = (mensaje: string) => {
        setPeticion(null);
        setAlert({ open: true, severity: 'success', msg: mensaje });
        cargar(mes);
    };

    const mesActual = datos ? mesDe(datos.hoy) : null;
    const seleccionada = datos?.personas.find(p => p.especialistaId === persona) ?? null;
    const editable = !!datos?.activo;

    const situacionChip = (p: IPersonaRegistros) => p.situacion && <Chip size="small" variant="outlined" label={SITUACION[p.situacion]} />;

    // ---------- Todo el equipo: una fila por persona ----------
    const equipo = (d: IJornadaRegistros) => {
        if (d.personas.length === 0) return <Typography variant="body2" color="text.secondary">No hay registros este mes.</Typography>;
        const avisos = (p: IPersonaRegistros) => (
            <>
                {p.totales.incompletas > 0 && <Chip size="small" color="warning" label={`${p.totales.incompletas} ${p.totales.incompletas > 1 ? 'días incompletos' : 'día incompleto'}`} sx={{ mr: 0.5 }} />}
                {p.totales.sinFichajes > 0 && <Chip size="small" variant="outlined" label={`${p.totales.sinFichajes} ${p.totales.sinFichajes > 1 ? 'días' : 'día'} sin fichajes`} />}
            </>
        );
        const quien = (p: IPersonaRegistros) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: p.color, width: 32, height: 32, fontSize: '0.8rem' }}>{iniciales(p)}</Avatar>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{nombreDe(p)}</Typography>
                {situacionChip(p)}
            </Box>
        );
        if (esMovil) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {d.personas.map(p => (
                        <Paper key={p.especialistaId} variant="outlined" sx={{ px: 1.5, py: 1, cursor: 'pointer' }} onClick={() => setPersona(p.especialistaId)}>
                            {quien(p)}
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {p.totales.previstoMs
                                    ? <>{duracionFichada(p.totales.trabajadoMs)} de {duracionFichada(p.totales.previstoMs)} · <Box component="span" sx={{ color: colorDiferencia(p.totales.diferenciaMs) }}>{diferenciaFichada(p.totales.diferenciaMs)}</Box></>
                                    : `${duracionFichada(p.totales.trabajadoMs)} · sin horas previstas`}
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>{avisos(p)}</Box>
                        </Paper>
                    ))}
                </Box>
            );
        }
        return (
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Persona</TableCell>
                        <TableCell align="right">Fichado</TableCell>
                        <TableCell align="right">Previsto</TableCell>
                        <TableCell align="right">Diferencia</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {d.personas.map(p => (
                        <TableRow key={p.especialistaId} hover sx={{ cursor: 'pointer' }} onClick={() => setPersona(p.especialistaId)}>
                            <TableCell>{quien(p)}</TableCell>
                            <TableCell align="right">{duracionFichada(p.totales.trabajadoMs)}</TableCell>
                            <TableCell align="right">{p.totales.previstoMs ? duracionFichada(p.totales.previstoMs) : '—'}</TableCell>
                            {/* Sin horas previstas, la diferencia no dice nada */}
                            <TableCell align="right" sx={{ color: p.totales.previstoMs ? colorDiferencia(p.totales.diferenciaMs) : undefined }}>{p.totales.previstoMs ? diferenciaFichada(p.totales.diferenciaMs) : '—'}</TableCell>
                            <TableCell>{avisos(p)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    };

    // ---------- Una persona: su mes día a día ----------
    const fichaje = (p: IPersonaRegistros, dia: IDiaFichajes, e: IJornadaEvento) => {
        const correccion = textoCorreccion(e, datos?.nombres, p.especialistaId);
        const marca = correccion ?? (e.origen === 'declarada' ? 'Hora declarada' : '');
        const chip = (
            <Chip size="small" variant="outlined" label={`${NOMBRE_FICHAJE[e.tipo]} ${horaFichaje(e.hora)}`}
                icon={correccion ? <AdminPanelSettingsOutlinedIcon /> : e.origen === 'declarada' ? <EditNoteIcon /> : undefined}
                onClick={editable ? (ev) => setMenu({ ancla: ev.currentTarget, evento: e, fecha: dia.fecha }) : undefined} />
        );
        return marca ? <Tooltip key={e._id} title={marca}>{chip}</Tooltip> : <Box component="span" key={e._id}>{chip}</Box>;
    };

    const detalle = (d: IJornadaRegistros, p: IPersonaRegistros) => {
        // Los días con algo que ver: fichajes, anulados o, ya pasados, horas previstas
        const dias = p.dias.filter(dia => dia.fecha <= d.hoy && (dia.eventos.length > 0 || dia.anulados.length > 0 || (dia.fecha < d.hoy && dia.previstoMs > 0)));
        return (
            <Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => setPersona(null)}>Todo el equipo</Button>
                    <TextField select size="small" value={p.especialistaId} sx={{ minWidth: 220 }} onChange={(ev) => setPersona(Number(ev.target.value))}>
                        {d.personas.map(o => <MenuItem key={o.especialistaId} value={o.especialistaId}>{nombreDe(o)}{o.situacion ? ` · ${SITUACION[o.situacion]}` : ''}</MenuItem>)}
                    </TextField>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <CifrasJornada cifras={[
                        { etiqueta: 'Fichado', valor: duracionFichada(p.totales.trabajadoMs) },
                        { etiqueta: 'Previsto', valor: p.totales.previstoMs ? duracionFichada(p.totales.previstoMs) : '—' },
                        { etiqueta: 'Diferencia', valor: p.totales.previstoMs ? diferenciaFichada(p.totales.diferenciaMs) : '—', color: p.totales.previstoMs ? colorDiferencia(p.totales.diferenciaMs) : undefined },
                    ]} />
                    {mes === mesActual && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Totales de los días terminados: hoy cuenta al cerrar la jornada</Typography>}
                </Box>
                {dias.length === 0 && <Typography variant="body2" color="text.secondary">No hay fichajes este mes.</Typography>}
                {dias.map(dia => (
                    <Paper variant="outlined" key={dia.fecha} sx={{ px: 2, py: 1.5, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1">{diaSemanaCorto(dia.fecha, true)}</Typography>
                            {dia.esFestivo && <Chip size="small" label="Festivo" />}
                            {dia.estado === 'incompleta' && <Chip size="small" color="warning" label="Incompleta" />}
                            {dia.eventos.length === 0 && dia.fecha < d.hoy && <Chip size="small" variant="outlined" label="Sin fichajes" />}
                            {/* Sin fichajes o incompleto (no hay nada fichado que contar), solo lo previsto; si no, lo fichado frente a
                                lo previsto */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                                {dia.eventos.length === 0 || dia.estado === 'incompleta'
                                    ? (dia.previstoMs > 0 ? `Previsto ${duracionFichada(dia.previstoMs)}` : '')
                                    : duracionFichada(dia.trabajadoMs)}
                                {dia.previstoMs > 0 && <>
                                    {dia.eventos.length > 0 && dia.estado !== 'incompleta' && ` de ${duracionFichada(dia.previstoMs)}`}
                                    {(dia.fecha < d.hoy || dia.estado === 'cerrada') && <> · <Box component="span" sx={{ color: colorDiferencia(dia.diferenciaMs) }}>{diferenciaFichada(dia.diferenciaMs)}</Box></>}
                                </>}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.75 }}>
                            {dia.eventos.map(e => fichaje(p, dia, e))}
                            {dia.anulados.map(a => <ChipAnulado key={a._id} size="small" texto={`${NOMBRE_FICHAJE[a.tipo]} ${horaFichaje(a.hora)}`} detalle={textoAnulado(a, d.nombres, p.especialistaId)} />)}
                            {editable && (
                                <Button size="small" startIcon={<AddIcon />}
                                    onClick={() => setPeticion({ accion: 'anadir', especialistaId: p.especialistaId, nombre: nombreDe(p), fecha: dia.fecha, tipo: dia.estado === 'incompleta' ? 'salida' : 'entrada' })}>
                                    Añadir fichaje
                                </Button>
                            )}
                        </Box>
                    </Paper>
                ))}
            </Box>
        );
    };

    return (
        <Box>
            {datos && !datos.activo && (
                <Alert severity="info" sx={{ mb: 2 }}>El registro de jornada está desactivado: los registros se pueden consultar, pero no corregir.</Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <IconButton aria-label="Mes anterior" disabled={loading || !mes || !datos || mes <= datos.mesMinimo} onClick={() => mes && cargar(mesAnterior(mes))}><ChevronLeftIcon /></IconButton>
                <Typography variant="h6" sx={{ minWidth: 180, textAlign: 'center' }}>{mes ? nombreMes(mes) : ''}</Typography>
                <IconButton aria-label="Mes siguiente" disabled={loading || !mes || mes === mesActual} onClick={() => mes && cargar(siguienteMes(mes))}><ChevronRightIcon /></IconButton>
                {loading && datos && <CircularProgress size={20} />}
                {/* Exporta lo que se está viendo: la persona elegida o todo el equipo */}
                {mes && datos && <Box sx={{ ml: 'auto' }}><ExportarJornada mes={mes} especialistaId={seleccionada?.especialistaId} setAlert={setAlert} /></Box>}
            </Box>

            {loading && !datos ? <CircularProgress /> : datos && (seleccionada ? detalle(datos, seleccionada) : equipo(datos))}

            {/* ACCIONES DE UN FICHAJE */}
            <Menu anchorEl={menu?.ancla} open={menu !== null} onClose={() => setMenu(null)}>
                {menu && seleccionada && [
                    <MenuItem key="corregir" onClick={() => { setPeticion({ accion: 'corregir', especialistaId: seleccionada.especialistaId, nombre: nombreDe(seleccionada), fecha: menu.fecha, evento: menu.evento, hora: aHHmm(new Date(menu.evento.hora)) }); setMenu(null); }}>Corregir la hora</MenuItem>,
                    ...(menu.evento.origen === 'declarada'
                        ? [<MenuItem key="confirmar" onClick={() => { setPeticion({ accion: 'confirmar', especialistaId: seleccionada.especialistaId, nombre: nombreDe(seleccionada), fecha: menu.fecha, evento: menu.evento }); setMenu(null); }}>Confirmar la hora</MenuItem>]
                        : []),
                    <MenuItem key="anular" onClick={() => { setPeticion({ accion: 'anular', especialistaId: seleccionada.especialistaId, nombre: nombreDe(seleccionada), fecha: menu.fecha, evento: menu.evento }); setMenu(null); }}>Anular</MenuItem>,
                ]}
            </Menu>

            <CorreccionDialog peticion={peticion} onClose={() => setPeticion(null)} onGuardado={guardado} pausas={datos?.pausas === true} />
        </Box>
    );
};

export default RegistrosJornada;
